import { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { ErrorStateCard } from '@/components/ui/ErrorStateCard';
import { localizedApiErrorMessage } from '@/lib/utils';
import { navigateToStevens } from '@/lib/chat-navigation';
import { triggerBlobDownload } from '@/lib/download';
import { useCompanionName } from '@/hooks/useCompanionName';
import { api } from '@/services/api';
import { useFamilyMine } from '@/hooks/useFamilyMine';
import { useDashboardMode } from '@/hooks/useDashboardMode';
import { HealthMemorialView } from '@/components/medical/HealthMemorialView';
import {
  MedicationCard,
  QuestionCard,
  SymptomEntryRow,
} from '@/components/medical/MedicalCards';
import { MedicalHeader } from '@/components/medical/MedicalChrome';
import { FieldLabel, PageWithAside } from '@/components/editorial';
import {
  MedicalStevensCallout,
  buildMedicalPatterns,
} from '@/components/medical/MedicalStevensCallout';
import { AddMedicationModal } from '@/components/medical/AddMedicationModal';
import { AddQuestionModal } from '@/components/medical/AddQuestionModal';
import { LogSymptomModal } from '@/components/medical/LogSymptomModal';
import { ConfirmActionDialog } from '@/components/ui/ConfirmActionDialog';
import {
  MedicationDetailDialog,
  QuestionDetailDialog,
  SymptomDetailDialog,
} from '@/components/medical/RecordDetailDialogs';
import { ToastAction } from '@/components/ui/toast';
import {
  DEFAULT_FILTERS,
  hasUnreportedSevere,
  isMedicalSection,
  loadMedicalSection,
  medicationMatches,
  persistMedicalSection,
  questionMatches,
  symptomMatches,
  symptomsToCsv,
  type MedicalFilters,
  type MedicalSection,
} from '@/lib/medical-display';
import type {
  MedicalAppointment,
  Medication,
  DoctorQuestion,
  SymptomEntry,
} from '@/types/api';

// A row's trash asks first (D-C: the in-app confirm, never browser confirm()),
// then runs the same delete mutation the detail dialog runs.
type PendingDelete =
  | { kind: 'medication'; record: Medication }
  | { kind: 'question'; record: DoctorQuestion }
  | { kind: 'symptom'; record: SymptomEntry };

// ── Component ────────────────────────────────────────────────────────────

export function HealthPage() {
  const { t, i18n } = useTranslation(['health', 'common']);
  const { toast } = useToast();
  // Non-English toasts never show a server English sentence (R3-B11).
  const failure = (error: unknown) =>
    localizedApiErrorMessage(error, t, i18n.language) || t('health:toast.pleaseTryAgain');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const companionName = useCompanionName();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryTab = searchParams.get('tab');
  const initialSection: MedicalSection = isMedicalSection(queryTab) ? queryTab : loadMedicalSection();
  const [section, setSectionState] = useState<MedicalSection>(initialSection);
  const prefillMedicationId = searchParams.get('medication') || '';

  const setTab = (tab: string) => {
    const valid = isMedicalSection(tab) ? tab : 'medications';
    setSectionState(valid);
    persistMedicalSection(valid);
    const next = new URLSearchParams(searchParams);
    next.set('tab', valid);
    if (valid !== 'symptoms') next.delete('medication');
    // A detail view belongs to the tab it was opened on — switching tabs
    // closes it rather than reinterpreting the id against another record type.
    next.delete('id');
    setSearchParams(next, { replace: true });
  };

  // ── Record detail views (#847, D-F) ──
  //
  // Deep-linkable: `?tab=X&id=Y` opens record Y's detail view on tab X —
  // the same URL pattern the page already uses for `?tab` + `?medication`.
  // An id that matches nothing in the loaded collection renders nothing.
  const detailId = searchParams.get('id');

  const openDetail = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', id);
    setSearchParams(next, { replace: true });
  };

  const closeDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  };

  // Keep URL + local state in sync if the user updates ?tab manually
  useEffect(() => {
    if (isMedicalSection(queryTab) && queryTab !== section) {
      setSectionState(queryTab);
      persistMedicalSection(queryTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryTab]);

  const currentTab: MedicalSection = section;

  // ── Toolbar state (2026-09-09) ──
  const [filters, setFilters] = useState<MedicalFilters>(DEFAULT_FILTERS);
  const [search, setSearch] = useState('');

  // ── Dialogs ──
  const [showMedicationDialog, setShowMedicationDialog] = useState(false);
  const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<DoctorQuestion | null>(null);
  const [showSymptomDialog, setShowSymptomDialog] = useState(false);
  const [editingSymptom, setEditingSymptom] = useState<SymptomEntry | null>(null);
  const [answeringQuestion, setAnsweringQuestion] = useState<string | null>(null);
  const [answerNotes, setAnswerNotes] = useState('');
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  // Optional prefill payload for the symptom modal — set when the user clicks
  // "Log reaction" on a medication row. Cleared when the modal closes.
  const [symptomPrefill, setSymptomPrefill] = useState<{
    type?: SymptomEntry['type'];
    linked_medication_id?: string;
  } | null>(
    prefillMedicationId
      ? { type: 'medication_reaction', linked_medication_id: prefillMedicationId }
      : null,
  );

  // Optional question prefill — set when a Stevens pattern jumps to the
  // questions tab with a drafted question. Cleared when the modal closes.
  const [questionPrefill, setQuestionPrefill] = useState<string | null>(null);

  // ── Queries ──────────────────────────────────────────────────────
  //
  // Appointments are read here only: questions link to them ("For Thu Sep 12")
  // and the companion's prep rule reads them. Their home — add, edit, status,
  // delete — is the calendar (2026-09-09). Same cache key as the calendar and
  // the Today card, so a change there reaches this page too.

  const { data: appointmentsResponse } = useQuery({
    queryKey: ['medical', 'appointments'],
    queryFn: () => api.medical.appointments.getAll(),
  });

  const {
    data: medicationsResponse,
    isLoading: loadingMeds,
    error: errorMeds,
  } = useQuery({
    queryKey: ['medical', 'medications'],
    queryFn: () => api.medical.medications.getAll(),
  });

  const {
    data: questionsResponse,
    isLoading: loadingQuestions,
    error: errorQuestions,
  } = useQuery({
    queryKey: ['medical', 'questions'],
    queryFn: () => api.medical.questions.getAll(),
  });

  const {
    data: symptomsResponse,
    isLoading: loadingSymptoms,
    error: errorSymptoms,
  } = useQuery({
    queryKey: ['medical', 'symptoms'],
    queryFn: () => api.medical.symptoms.getAll(),
  });

  const appointments = useMemo<MedicalAppointment[]>(() => appointmentsResponse?.data ?? [], [appointmentsResponse]);
  const medications = useMemo<Medication[]>(() => medicationsResponse?.data ?? [], [medicationsResponse]);
  const questions = useMemo<DoctorQuestion[]>(() => questionsResponse?.data ?? [], [questionsResponse]);
  const symptoms = useMemo<SymptomEntry[]>(() => symptomsResponse?.data ?? [], [symptomsResponse]);

  // ── Mutations ────────────────────────────────────────────────────

  const createMedication = useMutation({
    mutationFn: (data: Partial<Medication>) => api.medical.medications.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'medications'] });
      setShowMedicationDialog(false);
      toast({ title: t('health:toast.medication.added'), description: t('health:toast.medication.addedBody') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.medication.addFailed'), description: failure(error) });
    },
  });

  const updateMedication = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Medication> }) =>
      api.medical.medications.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'medications'] });
      setShowMedicationDialog(false);
      setEditingMedication(null);
      toast({ title: t('health:toast.medication.updated'), description: t('health:toast.changesSaved') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.medication.updateFailed'), description: failure(error) });
    },
  });

  // Archive-over-delete (D-C): "Mark inactive" flips `active` AND stamps
  // end_date, so the row's "Ended <date>" line is true. Reactivating clears
  // the end date. Goes through PUT (not the bare toggle route) because the
  // toggle route can't carry the date.
  const toggleMedication = useMutation({
    mutationFn: (med: Medication) =>
      api.medical.medications.update(
        med._id,
        med.active
          ? { active: false, end_date: new Date().toISOString().slice(0, 10) }
          : { active: true, end_date: '' },
      ),
    onSuccess: (_data, med) => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'medications'] });
      toast({
        title: med.active ? t('health:toast.medication.deactivated') : t('health:toast.medication.reactivated'),
        description: med.active
          ? t('health:toast.medication.deactivatedBody')
          : t('health:toast.medication.reactivatedBody'),
      });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.medication.updateFailed'), description: failure(error) });
    },
  });

  // No undo toast here: re-creating would mint a new id, so the dose log and
  // any symptom links would NOT come back — an "Undo" that silently restored
  // less than it deleted. The confirm dialog says exactly what is lost;
  // "Mark inactive" is the recoverable path (D-C).
  const deleteMedication = useMutation({
    mutationFn: (med: Medication) => api.medical.medications.delete(med._id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'medications'] });
      closeDetail();
      toast({ title: t('health:toast.medication.removed'), description: t('health:toast.medication.removedBody') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.medication.deleteFailed'), description: failure(error) });
    },
  });

  const createQuestion = useMutation({
    mutationFn: (data: Partial<DoctorQuestion>) => api.medical.questions.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'questions'] });
      setShowQuestionDialog(false);
      setQuestionPrefill(null);
      toast({ title: t('health:toast.question.added'), description: t('health:toast.question.addedBody') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.question.addFailed'), description: failure(error) });
    },
  });

  const markQuestionAnswered = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      api.medical.questions.markAnswered(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'questions'] });
      setAnsweringQuestion(null);
      setAnswerNotes('');
      toast({ title: t('health:toast.question.answered'), description: t('health:toast.question.answeredBody') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.question.answerFailed'), description: failure(error) });
    },
  });

  const updateQuestion = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DoctorQuestion> }) =>
      api.medical.questions.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'questions'] });
      setShowQuestionDialog(false);
      setEditingQuestion(null);
      toast({ title: t('health:toast.question.updated'), description: t('health:toast.changesSaved') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.question.updateFailed'), description: failure(error) });
    },
  });

  // Undo here is honest: nothing references a question's id, and re-creating
  // (plus re-marking answered) restores every user-visible field. The new row
  // gets a new id and created_at — invisible to the parent.
  const undoDeleteQuestion = async (q: DoctorQuestion) => {
    try {
      const payload: Partial<DoctorQuestion> = {
        question: q.question,
        priority: q.priority,
        category: q.category,
      };
      if (q.linked_appointment_id) payload.linked_appointment_id = q.linked_appointment_id;
      const res = await api.medical.questions.create(payload);
      if (q.answered && res.data?._id) {
        await api.medical.questions.markAnswered(res.data._id, q.answer_notes || '');
      }
      queryClient.invalidateQueries({ queryKey: ['medical', 'questions'] });
      toast({ title: t('health:toast.question.restored'), description: t('health:toast.question.restoredBody') });
    } catch (error) {
      toast({ variant: 'destructive', title: t('health:toast.question.restoreFailed'), description: failure(error) });
    }
  };

  const deleteQuestion = useMutation({
    mutationFn: (q: DoctorQuestion) => api.medical.questions.delete(q._id),
    onSuccess: (_data, q) => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'questions'] });
      closeDetail();
      toast({
        title: t('health:toast.question.deleted'),
        description: t('health:toast.question.deletedBody'),
        action: (
          <ToastAction altText={t('health:toast.undoAlt')} onClick={() => undoDeleteQuestion(q)}>
            {t('common:actions.undo')}
          </ToastAction>
        ),
      });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.question.deleteFailed'), description: failure(error) });
    },
  });

  const createSymptom = useMutation({
    mutationFn: (data: Partial<SymptomEntry>) => api.medical.symptoms.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'symptoms'] });
      setShowSymptomDialog(false);
      setSymptomPrefill(null);
      toast({ title: t('health:toast.symptom.logged'), description: t('health:toast.symptom.loggedBody') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.symptom.logFailed'), description: failure(error) });
    },
  });

  const updateSymptom = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SymptomEntry> }) =>
      api.medical.symptoms.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'symptoms'] });
      setShowSymptomDialog(false);
      setEditingSymptom(null);
      toast({ title: t('health:toast.symptom.updated'), description: t('health:toast.changesSaved') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.symptom.updateFailed'), description: failure(error) });
    },
  });

  // Undo is honest for symptoms too: re-creating restores every field the
  // entry carried (nothing references a symptom's id).
  const undoDeleteSymptom = async (s: SymptomEntry) => {
    try {
      const payload: Partial<SymptomEntry> = {
        date: s.date,
        type: s.type,
        description: s.description,
        severity: s.severity,
        what_helped: s.what_helped,
        reported_to_doctor: s.reported_to_doctor,
        doctor_response: s.doctor_response,
      };
      if (s.linked_medication_id) payload.linked_medication_id = s.linked_medication_id;
      if (s.linked_appointment_id) payload.linked_appointment_id = s.linked_appointment_id;
      await api.medical.symptoms.create(payload);
      queryClient.invalidateQueries({ queryKey: ['medical', 'symptoms'] });
      toast({ title: t('health:toast.symptom.restored'), description: t('health:toast.symptom.restoredBody') });
    } catch (error) {
      toast({ variant: 'destructive', title: t('health:toast.symptom.restoreFailed'), description: failure(error) });
    }
  };

  const deleteSymptom = useMutation({
    mutationFn: (s: SymptomEntry) => api.medical.symptoms.delete(s._id),
    onSuccess: (_data, s) => {
      queryClient.invalidateQueries({ queryKey: ['medical', 'symptoms'] });
      closeDetail();
      toast({
        title: t('health:toast.symptom.deleted'),
        description: t('health:toast.symptom.deletedBody'),
        action: (
          <ToastAction altText={t('health:toast.undoAlt')} onClick={() => undoDeleteSymptom(s)}>
            {t('common:actions.undo')}
          </ToastAction>
        ),
      });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.symptom.deleteFailed'), description: failure(error) });
    },
  });

  // CSV, not raw JSON (#847): the audience is an oncologist reading a
  // parent's handout. The backend export shape is transformed client-side —
  // human headers, plain values.
  const exportSymptoms = useMutation({
    mutationFn: () => api.medical.symptoms.export(),
    onSuccess: (data) => {
      // The meds list resolves each row's linked_medication_id to a name —
      // the CSV never prints a raw id (blank when unlinked or med deleted).
      const csv = symptomsToCsv(data.data ?? [], medications, t);
      triggerBlobDownload(
        new Blob([csv], { type: 'text/csv;charset=utf-8' }),
        `symptom-log-${new Date().toISOString().slice(0, 10)}.csv`,
      );
      toast({ title: t('health:toast.export.done'), description: t('health:toast.export.doneBody') });
    },
    onError: (error: Error) => {
      toast({ variant: 'destructive', title: t('health:toast.export.failed'), description: failure(error) });
    },
  });

  // ── Handlers ──

  function openEditMedication(med: Medication) {
    setEditingMedication(med);
    setShowMedicationDialog(true);
  }

  function openEditQuestion(q: DoctorQuestion) {
    setEditingQuestion(q);
    setShowQuestionDialog(true);
  }

  function openEditSymptom(s: SymptomEntry) {
    setEditingSymptom(s);
    setShowSymptomDialog(true);
  }

  // "Add follow-up" on an answered question (#847): opens the create modal
  // with the original question quoted, so the thread stays visible.
  function openFollowUpQuestion(q: DoctorQuestion) {
    closeDetail();
    setQuestionPrefill(t('health:page.followUpPrefill', { question: q.question }));
    setShowQuestionDialog(true);
  }

  function handleLogReaction(medicationId: string) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'symptoms');
    next.set('medication', medicationId);
    setSearchParams(next, { replace: true });
    setSectionState('symptoms');
    persistMedicalSection('symptoms');
    setSymptomPrefill({ type: 'medication_reaction', linked_medication_id: medicationId });
    setShowSymptomDialog(true);
  }

  // Open the create dialog for a given section, switching the tab to it
  // first so the new record lands where the parent is looking. `setTab`
  // keeps the three tab stores in sync (state + localStorage + ?tab=).
  // The toolbar's `+` routes every section through here (#848: every create
  // action reachable from any tab).
  const handleAddFor = (s: MedicalSection) => {
    setTab(s);
    if (s === 'medications') {
      setEditingMedication(null);
      setShowMedicationDialog(true);
    } else if (s === 'questions') {
      setEditingQuestion(null);
      setShowQuestionDialog(true);
    } else {
      setEditingSymptom(null);
      setSymptomPrefill(null);
      setShowSymptomDialog(true);
    }
  };

  const handleStevensJump = (s: MedicalSection, opts?: { draftQuestion?: string }) => {
    setTab(s);
    if (s === 'questions' && opts?.draftQuestion) {
      setQuestionPrefill(opts.draftQuestion);
      setShowQuestionDialog(true);
    }
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    if (pendingDelete.kind === 'medication') deleteMedication.mutate(pendingDelete.record);
    else if (pendingDelete.kind === 'question') deleteQuestion.mutate(pendingDelete.record);
    else deleteSymptom.mutate(pendingDelete.record);
    setPendingDelete(null);
  };

  // ── Computed ─────────────────────────────────────────────────────

  const { data: familyResp } = useFamilyMine();
  const patient = familyResp?.data?.patient;

  // The patterns carry Intl output (a clock time, "in 3 days") in the
  // language active when they were built, so a language switch rebuilds them.
  const language = i18n.language;
  const stevensPatterns = useMemo(
    () => buildMedicalPatterns({ appointments, medications, symptoms, patient }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointments, medications, symptoms, patient, language],
  );

  const symptomBannerVisible = hasUnreportedSevere(symptoms);

  const linkedMedById = useMemo(() => new Map(medications.map((m) => [m._id, m])), [medications]);
  const linkedApptById = useMemo(() => new Map(appointments.map((a) => [a._id, a])), [appointments]);

  const visibleMeds = useMemo(
    () =>
      medications
        .filter((m) => (filters.medScope === 'active' ? m.active : !m.active))
        .filter((m) => medicationMatches(m, search)),
    [medications, filters.medScope, search],
  );

  const visibleQuestions = useMemo(
    () =>
      questions
        .filter((q) => (filters.qScope === 'pending' ? !q.answered : q.answered))
        .filter((q) => questionMatches(q, search)),
    [questions, filters.qScope, search],
  );
  const thisVisitQs = useMemo(() => visibleQuestions.filter((q) => q.priority === 'this_visit'), [visibleQuestions]);
  const canWaitQs = useMemo(() => visibleQuestions.filter((q) => q.priority !== 'this_visit'), [visibleQuestions]);

  const visibleSymptoms = useMemo(() => {
    let arr = [...symptoms];
    if (filters.symScope === 'notable') arr = arr.filter((s) => s.severity === 'severe' || s.severity === 'moderate');
    else if (filters.symScope === 'reactions') arr = arr.filter((s) => s.type === 'medication_reaction');
    if (filters.severity !== 'all') arr = arr.filter((s) => s.severity === filters.severity);
    arr = arr.filter((s) =>
      symptomMatches(s, search, s.linked_medication_id ? linkedMedById.get(s.linked_medication_id)?.medication_name : undefined),
    );
    return arr.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [symptoms, filters.symScope, filters.severity, search, linkedMedById]);

  // Hardcoded stub: the page is only routed to parents today. If a staff
  // read-only view ever lands, derive this from useAuth() instead.
  const isParent = true;

  const isLoadingAny =
    (currentTab === 'medications' && loadingMeds) ||
    (currentTab === 'questions' && loadingQuestions) ||
    (currentTab === 'symptoms' && loadingSymptoms);

  const errorAny =
    (currentTab === 'medications' && errorMeds) ||
    (currentTab === 'questions' && errorQuestions) ||
    (currentTab === 'symptoms' && errorSymptoms);

  // Resolve `?id=` against the ACTIVE tab's collection only. A stale or
  // foreign id resolves to nothing and no detail view renders — deep links
  // degrade silently instead of erroring.
  const detailMed = currentTab === 'medications' && detailId ? linkedMedById.get(detailId) : undefined;
  const detailQuestion = currentTab === 'questions' && detailId ? questions.find((q) => q._id === detailId) : undefined;
  const detailSymptom = currentTab === 'symptoms' && detailId ? symptoms.find((s) => s._id === detailId) : undefined;

  // ── The memorial gate (#693) ──────────────────────────────────────────
  //
  // #693 named /tasks, /calendar and the dashboard. It did not name this
  // page, and this page is the surface where the defect is loudest: three
  // sections of live treatment management, each with an empty state written
  // as an invitation to log more of it. Reached through the same shared
  // accessor as the other three so there is one predicate, not a fourth.
  //
  // Both branches sit below every hook in this component on purpose — an
  // early return above `useState`/`useMemo` would change the hook order
  // between renders the moment the family record lands.
  const { isMemorial, isLoading: modeLoading } = useDashboardMode();

  // Appointments moved to the calendar (2026-09-09). Old links — the Today
  // card, chat, bookmarks — carry `?tab=appointments[&id=…]`; send them to
  // the record's home with the id intact.
  if (queryTab === 'appointments') {
    const id = searchParams.get('id');
    return <Navigate to={id ? `/calendar?id=${id}` : '/calendar'} replace />;
  }

  // Held, not rendered-then-retracted: `resolveDashboardMode` fails safe to
  // 'active', so painting the treatment page first would flash a dead child's
  // medication list for a beat. The whole surface is treatment content here,
  // so unlike /tasks there is nothing safe to show while we wait.
  if (modeLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-stevens-ink-3" />
      </div>
    );
  }

  if (isMemorial) {
    return <HealthMemorialView childName={patient?.name} />;
  }

  const searching = search.trim().length > 0;
  const hasCallout = stevensPatterns.length > 0;

  return (
    // One column, unless the companion has something to say — then her card
    // takes a right column (option D of the 2026-09-09 placement study). The
    // dashboard-style companion rail that used to sit there duplicated the
    // floating widget and is gone. Memorial gating stays page-level: the early
    // returns above never let the aside render for a bereaved family.
    <PageWithAside
      aside={hasCallout ? (
        <MedicalStevensCallout patterns={stevensPatterns} onJump={handleStevensJump} layout="card" />
      ) : null}
    >
    <div className="space-y-6">
      <MedicalHeader
        section={currentTab}
        onSectionChange={setTab}
        filters={filters}
        onFiltersChange={setFilters}
        search={search}
        onSearchChange={setSearch}
        onAddFor={handleAddFor}
        onPrint={() => window.print()}
        onExport={symptoms.length > 0 && !exportSymptoms.isPending ? () => exportSymptoms.mutate() : undefined}
        isParent={isParent}
      />

      {isLoadingAny ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-stevens-ink-3" />
        </div>
      ) : errorAny ? (
        <ErrorStateCard message={t('health:page.loadFailed')} />
      ) : currentTab === 'medications' ? (
        <section className="space-y-4">
          {visibleMeds.length === 0 ? (
            searching ? (
              <NoMatch query={search} />
            ) : (
              <EmptyHint
                title={filters.medScope === 'active' ? t('health:page.empty.activeMedsTitle') : t('health:page.empty.inactiveMedsTitle')}
                body={filters.medScope === 'active'
                  ? t('health:page.empty.activeMedsBody', { companion: companionName })
                  : t('health:page.empty.inactiveMedsBody')}
              />
            )
          ) : (
            <div className="grid gap-3">
              {visibleMeds.map((m) => (
                <MedicationCard
                  key={m._id}
                  med={m}
                  symptoms={symptoms}
                  onLogReaction={(med) => handleLogReaction(med._id)}
                  onOpen={(med) => openDetail(med._id)}
                  onEdit={openEditMedication}
                  onDelete={(med) => setPendingDelete({ kind: 'medication', record: med })}
                />
              ))}
            </div>
          )}
        </section>
      ) : currentTab === 'questions' ? (
        <section className="space-y-4">
          {filters.qScope === 'pending' ? (
            visibleQuestions.length === 0 ? (
              searching ? (
                <NoMatch query={search} />
              ) : (
                <EmptyHint title={t('health:page.empty.pendingQuestionsTitle')} body={t('health:page.empty.pendingQuestionsBody')} />
              )
            ) : (
              <div className="space-y-8">
                {thisVisitQs.length > 0 && (
                  <Lane i18nKey="health:page.lane.thisVisit">
                    <div className="grid gap-3 md:grid-cols-2">
                      {thisVisitQs.map((q) => (
                        <QuestionCardWrapper
                          key={q._id}
                          q={q}
                          linkedAppt={q.linked_appointment_id ? linkedApptById.get(q.linked_appointment_id) : undefined}
                          answeringQuestion={answeringQuestion}
                          answerNotes={answerNotes}
                          setAnsweringQuestion={setAnsweringQuestion}
                          setAnswerNotes={setAnswerNotes}
                          markQuestionAnswered={markQuestionAnswered}
                          onOpen={(qq) => openDetail(qq._id)}
                          onEdit={openEditQuestion}
                          onDelete={(qq) => setPendingDelete({ kind: 'question', record: qq })}
                        />
                      ))}
                    </div>
                  </Lane>
                )}
                {canWaitQs.length > 0 && (
                  <Lane i18nKey="health:page.lane.canWait">
                    <div className="grid gap-3 md:grid-cols-2">
                      {canWaitQs.map((q) => (
                        <QuestionCardWrapper
                          key={q._id}
                          q={q}
                          linkedAppt={q.linked_appointment_id ? linkedApptById.get(q.linked_appointment_id) : undefined}
                          answeringQuestion={answeringQuestion}
                          answerNotes={answerNotes}
                          setAnsweringQuestion={setAnsweringQuestion}
                          setAnswerNotes={setAnswerNotes}
                          markQuestionAnswered={markQuestionAnswered}
                          onMoveToVisit={(qq) =>
                            api.medical.questions.update(qq._id, { priority: 'this_visit' }).then(() =>
                              queryClient.invalidateQueries({ queryKey: ['medical', 'questions'] }),
                            )
                          }
                          onOpen={(qq) => openDetail(qq._id)}
                          onEdit={openEditQuestion}
                          onDelete={(qq) => setPendingDelete({ kind: 'question', record: qq })}
                        />
                      ))}
                    </div>
                  </Lane>
                )}
              </div>
            )
          ) : visibleQuestions.length === 0 ? (
            searching ? (
              <NoMatch query={search} />
            ) : (
              <EmptyHint title={t('health:page.empty.answeredQuestionsTitle')} body={t('health:page.empty.answeredQuestionsBody')} />
            )
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {/* Answered questions keep affordances (#847): follow-up on the
                  card, edit/delete on the row and in the detail view. */}
              {visibleQuestions.map((q) => (
                <QuestionCard
                  key={q._id}
                  q={q}
                  linkedAppt={q.linked_appointment_id ? linkedApptById.get(q.linked_appointment_id) : undefined}
                  onOpen={(qq) => openDetail(qq._id)}
                  onAddFollowUp={openFollowUpQuestion}
                  onEdit={openEditQuestion}
                  onDelete={(qq) => setPendingDelete({ kind: 'question', record: qq })}
                />
              ))}
            </div>
          )}
        </section>
      ) : (
        <section className="space-y-4">
          {symptomBannerVisible && (
            <div className="rounded-lg border border-status-danger-border/60 bg-status-danger/60 px-4 py-3.5 flex items-start gap-3">
              <span className="flex-shrink-0 mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-status-danger text-status-danger-fg">
                <AlertTriangle className="h-3.5 w-3.5" />
              </span>
              <div className="flex-1 min-w-0 text-body">
                <div className="font-medium text-status-danger-fg mb-0.5">{t('health:page.severeBanner.title')}</div>
                <p className="text-status-danger-fg leading-relaxed">
                  {t('health:page.severeBanner.body')}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-status-danger-border text-status-danger-fg hover:bg-status-danger/60"
                // Sent in the UI language (R3-B2): the label the family reads is the message.
                onClick={() => navigateToStevens(navigate, t('health:page.severeBanner.prompt'))}
              >
                {t('health:page.severeBanner.cta')}
              </Button>
            </div>
          )}

          {visibleSymptoms.length === 0 ? (
            searching || filters.symScope !== 'all' || filters.severity !== 'all' ? (
              <NoMatch query={search} />
            ) : (
              <EmptyHint title={t('health:page.empty.symptomsTitle')} body={t('health:page.empty.symptomsBody', { companion: companionName })} />
            )
          ) : (
            <div>
              {visibleSymptoms.map((s) => (
                <SymptomEntryRow
                  key={s._id}
                  s={s}
                  linkedMed={s.linked_medication_id ? linkedMedById.get(s.linked_medication_id) : undefined}
                  onOpen={(ss) => openDetail(ss._id)}
                  onEdit={openEditSymptom}
                  onDelete={(ss) => setPendingDelete({ kind: 'symptom', record: ss })}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <AddMedicationModal
        open={showMedicationDialog}
        onOpenChange={(o) => { setShowMedicationDialog(o); if (!o) setEditingMedication(null); }}
        editing={editingMedication}
        isSubmitting={createMedication.isPending || updateMedication.isPending}
        onSubmit={(data) => {
          if (editingMedication) {
            updateMedication.mutate({ id: editingMedication._id, data });
          } else {
            createMedication.mutate(data);
          }
        }}
      />

      <AddQuestionModal
        open={showQuestionDialog}
        onOpenChange={(o) => { setShowQuestionDialog(o); if (!o) { setQuestionPrefill(null); setEditingQuestion(null); } }}
        appointments={appointments}
        prefillQuestion={questionPrefill}
        editing={editingQuestion}
        isSubmitting={createQuestion.isPending || updateQuestion.isPending}
        onSubmit={(data) => {
          if (editingQuestion) {
            // Keep linked_appointment_id in the payload even when empty —
            // '' is how the backend clears an existing link.
            updateQuestion.mutate({ id: editingQuestion._id, data });
          } else {
            const payload: Partial<DoctorQuestion> = { ...data };
            if (!payload.linked_appointment_id) delete payload.linked_appointment_id;
            createQuestion.mutate(payload);
          }
        }}
      />

      <LogSymptomModal
        open={showSymptomDialog}
        onOpenChange={(o) => { setShowSymptomDialog(o); if (!o) { setSymptomPrefill(null); setEditingSymptom(null); } }}
        medications={medications}
        prefill={symptomPrefill ?? undefined}
        editing={editingSymptom}
        isSubmitting={createSymptom.isPending || updateSymptom.isPending}
        onSubmit={(data) => {
          if (editingSymptom) {
            // '' clears an existing link server-side; keep the keys.
            updateSymptom.mutate({ id: editingSymptom._id, data });
          } else {
            const payload: Partial<SymptomEntry> = { ...data };
            if (!payload.linked_medication_id) delete payload.linked_medication_id;
            if (!payload.linked_appointment_id) delete payload.linked_appointment_id;
            createSymptom.mutate(payload);
          }
        }}
      />

      {/* ── Row-level delete confirm (2026-09-09) — same copy as the detail
          dialogs, same honesty rules: a medication delete names what is lost
          and offers no undo; questions and symptoms get an honest Undo toast. */}
      <ConfirmActionDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={
          pendingDelete?.kind === 'medication' ? t('health:page.confirmDelete.medicationTitle', { name: pendingDelete.record.medication_name })
          : pendingDelete?.kind === 'question' ? t('health:page.confirmDelete.questionTitle')
          : t('health:page.confirmDelete.entryTitle')
        }
        description={
          pendingDelete?.kind === 'medication'
            ? t('health:page.confirmDelete.medicationBody')
            : pendingDelete?.kind === 'question'
              ? t('health:page.confirmDelete.questionBody')
              : t('health:page.confirmDelete.entryBody')
        }
        confirmLabel={
          pendingDelete?.kind === 'medication' ? t('health:page.confirmDelete.medicationConfirm')
          : pendingDelete?.kind === 'question' ? t('health:page.confirmDelete.questionConfirm')
          : t('health:page.confirmDelete.entryConfirm')
        }
        onConfirm={confirmDelete}
      />

      {/* ── Record detail views (#847, D-F) — deep-linked via ?tab&id ── */}

      {detailMed && (
        <MedicationDetailDialog
          open
          onOpenChange={(o) => { if (!o) closeDetail(); }}
          med={detailMed}
          symptoms={symptoms}
          onEdit={(m) => { openEditMedication(m); }}
          onToggleActive={(m) => toggleMedication.mutate(m)}
          onDelete={(m) => deleteMedication.mutate(m)}
          isMutating={toggleMedication.isPending || deleteMedication.isPending}
        />
      )}

      {detailQuestion && (
        <QuestionDetailDialog
          open
          onOpenChange={(o) => { if (!o) closeDetail(); }}
          q={detailQuestion}
          linkedAppt={detailQuestion.linked_appointment_id ? linkedApptById.get(detailQuestion.linked_appointment_id) : undefined}
          onEdit={(q) => { openEditQuestion(q); }}
          onDelete={(q) => deleteQuestion.mutate(q)}
          onAddFollowUp={openFollowUpQuestion}
          isMutating={deleteQuestion.isPending}
        />
      )}

      {detailSymptom && (
        <SymptomDetailDialog
          open
          onOpenChange={(o) => { if (!o) closeDetail(); }}
          s={detailSymptom}
          linkedMed={detailSymptom.linked_medication_id ? linkedMedById.get(detailSymptom.linked_medication_id) : undefined}
          onEdit={(s) => { openEditSymptom(s); }}
          onDelete={(s) => deleteSymptom.mutate(s)}
          isMutating={deleteSymptom.isPending}
        />
      )}
    </div>
    </PageWithAside>
  );
}
// ── Local helper components ───────────────────────────────────────────────

function EmptyHint({ title, body }: { title: string; body: string }) {
  return (
    <Card className="px-6 py-12 text-center shadow-none">
      <p className="text-card-title font-medium text-foreground mb-1.5">{title}</p>
      <p className="text-body text-stevens-ink-3 max-w-md mx-auto">{body}</p>
    </Card>
  );
}

function NoMatch({ query }: { query: string }) {
  const { t } = useTranslation(['health', 'common']);
  const q = query.trim();
  return (
    <Card className="px-6 py-10 text-center shadow-none">
      <p className="text-card-title font-medium text-foreground mb-1.5">
        {/* The quotes are the locale's, so they live in the catalog. */}
        {q ? t('health:page.noMatch.query', { query: q }) : t('health:page.noMatch.filters')}
      </p>
      <p className="text-body text-stevens-ink-3 max-w-md mx-auto">
        {q ? t('health:page.noMatch.queryHint') : t('health:page.noMatch.filtersHint')}
      </p>
    </Card>
  );
}

interface LaneProps {
  /** A whole heading with its `<em>` half marked up ("For <em>this visit</em>"). */
  i18nKey: 'health:page.lane.thisVisit' | 'health:page.lane.canWait';
  children: React.ReactNode;
}

// The lane heading names the group in words; the count that used to sit
// beside it restated the cards below (2026-09-09). One sentence per heading,
// the italic half a `<Trans>` slot, so a language can move it.
function Lane({ i18nKey, children }: LaneProps) {
  const { t } = useTranslation(['health', 'common']);
  return (
    <section>
      <header className="mb-4">
        <h2 className="text-xl sm:text-2xl font-medium text-foreground font-serif">
          <Trans
            t={t}
            i18nKey={i18nKey}
            components={{ em: <em className="font-normal italic text-foreground/80" /> }}
          />
        </h2>
      </header>
      {children}
    </section>
  );
}

interface QuestionCardWrapperProps {
  q: DoctorQuestion;
  linkedAppt?: MedicalAppointment;
  answeringQuestion: string | null;
  answerNotes: string;
  setAnsweringQuestion: (id: string | null) => void;
  setAnswerNotes: (s: string) => void;
  markQuestionAnswered: { mutate: (args: { id: string; notes?: string }) => void; isPending: boolean };
  onMoveToVisit?: (q: DoctorQuestion) => void;
  /** Opens the record's detail view. */
  onOpen?: (q: DoctorQuestion) => void;
  onEdit?: (q: DoctorQuestion) => void;
  onDelete?: (q: DoctorQuestion) => void;
}

function QuestionCardWrapper({
  q,
  linkedAppt,
  answeringQuestion,
  answerNotes,
  setAnsweringQuestion,
  setAnswerNotes,
  markQuestionAnswered,
  onMoveToVisit,
  onOpen,
  onEdit,
  onDelete,
}: QuestionCardWrapperProps) {
  const { t } = useTranslation(['health', 'common']);
  const isAnswering = answeringQuestion === q._id;
  return (
    <div className="space-y-2">
      <QuestionCard
        q={q}
        linkedAppt={linkedAppt}
        onMoveToVisit={onMoveToVisit}
        onAnswer={() => setAnsweringQuestion(q._id)}
        onOpen={onOpen}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {isAnswering && (
        <div className="rounded-lg border border-stevens-indigo/20 bg-stevens-indigo/5 p-3 space-y-2">
          <FieldLabel htmlFor={`ans-${q._id}`}>{t('health:page.answer.label')}</FieldLabel>
          <Textarea
            id={`ans-${q._id}`}
            value={answerNotes}
            onChange={(e) => setAnswerNotes(e.target.value)}
            placeholder={t('health:page.answer.placeholder')}
            rows={3}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setAnsweringQuestion(null); setAnswerNotes(''); }}
            >
              <X className="h-3.5 w-3.5 mr-1" /> {t('common:actions.cancel')}
            </Button>
            <Button
              size="sm"
              variant="brand"
              disabled={markQuestionAnswered.isPending || answerNotes.trim().length === 0}
              onClick={() => markQuestionAnswered.mutate({ id: q._id, notes: answerNotes })}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              {t('health:page.answer.save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
